<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-center">
      <div class="col-lg-10">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="mt-2 text-muted">Cargando información del cliente...</p>
        </div>

        <!-- Client Details -->
        <div v-else-if="currentClient">
          <!-- Page Header -->
          <div class="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h1 class="h3 mb-1">
                {{ currentClient.nombre }}
              </h1>
              <p class="text-muted mb-2">
                CUIT: <code>{{ currentClient.cuit }}</code>
              </p>
              <span
                class="badge fs-6"
                :class="currentClient.active ? 'bg-success' : 'bg-secondary'"
              >
                <i class="bi" :class="currentClient.active ? 'bi-check-circle' : 'bi-pause-circle'"></i>
                {{ currentClient.active ? 'Activo' : 'Inactivo' }}
              </span>
            </div>
            <div class="d-flex gap-2">
              <router-link
                :to="`/clients/${currentClient.id}/edit`"
                class="btn btn-primary"
              >
                <i class="bi bi-pencil me-1"></i>
                Editar
              </router-link>
              <router-link to="/clients" class="btn btn-outline-secondary">
                <i class="bi bi-arrow-left me-1"></i>
                Volver
              </router-link>
            </div>
          </div>

          <!-- Client Stats Cards -->
          <div class="row mb-4">
            <div class="col-md-4">
              <div class="card text-center">
                <div class="card-body">
                  <i class="bi bi-calendar-plus fs-2 text-primary mb-2"></i>
                  <h6 class="card-title">Fecha de Registro</h6>
                  <p class="card-text">
                    {{ formatDate(currentClient.created_at) }}
                  </p>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card text-center">
                <div class="card-body">
                  <i class="bi bi-calendar-check fs-2 text-success mb-2"></i>
                  <h6 class="card-title">Última Actualización</h6>
                  <p class="card-text">
                    {{ formatDate(currentClient.updated_at) }}
                  </p>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card text-center">
                <div class="card-body">
                  <i class="bi bi-truck-front fs-2 text-info mb-2"></i>
                  <h6 class="card-title">Total Dispositivos</h6>
                  <h3 class="card-text text-info">{{ currentClient.device_count || 0 }}</h3>
                </div>
              </div>
            </div>
          </div>

          <!-- Billing Information Card -->
          <div v-if="currentClient.fecha_inicio_facturacion || currentClient.cantidad_dias_corte" class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header">
                  <h5 class="mb-0">
                    <i class="bi bi-credit-card me-2"></i>
                    Información de Facturación
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <p class="mb-2"><strong>Fecha de Inicio de Facturación:</strong></p>
                      <p class="text-muted">
                        <i class="bi bi-calendar-event me-1"></i>
                        {{ currentClient.fecha_inicio_facturacion ? formatDate(currentClient.fecha_inicio_facturacion) : 'No especificada' }}
                      </p>
                    </div>
                    <div class="col-md-6">
                      <p class="mb-2"><strong>Días hasta Corte de Servicio:</strong></p>
                      <p class="text-muted">
                        <i class="bi bi-hourglass-split me-1"></i>
                        {{ currentClient.cantidad_dias_corte ? currentClient.cantidad_dias_corte + ' días' : 'No especificado' }}
                      </p>
                    </div>
                  </div>

                  <!-- Period Selection -->
                  <div class="row mt-4" v-if="currentClient.fecha_inicio_facturacion">
                    <div class="col-12">
                      <hr class="my-3">
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <h6 class="mb-0">
                          <i class="bi bi-calendar-range me-2"></i>
                          Periodo de Facturación
                        </h6>
                        <div class="btn-group" role="group">
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-primary"
                            @click="goToPreviousPeriod"
                            title="Periodo Anterior"
                          >
                            <i class="bi bi-chevron-left"></i>
                            Anterior
                          </button>
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-primary"
                            @click="goToNextPeriod"
                            title="Periodo Siguiente"
                          >
                            Siguiente
                            <i class="bi bi-chevron-right"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <label for="fecha_inicio_periodo" class="form-label">
                        <strong>Fecha de Inicio del Periodo:</strong>
                      </label>
                      <input
                        id="fecha_inicio_periodo"
                        v-model="fecha_inicio_periodo"
                        type="date"
                        class="form-control"
                      >
                      <div class="form-text">
                        <i class="bi bi-info-circle me-1"></i>
                        Fecha de inicio del periodo de facturación a consultar
                      </div>
                    </div>
                    <div class="col-md-6">
                      <label for="fecha_fin_periodo" class="form-label">
                        <strong>Fecha de Fin del Periodo:</strong>
                      </label>
                      <input
                        id="fecha_fin_periodo"
                        v-model="fecha_fin_periodo"
                        type="date"
                        class="form-control"
                      >
                      <div class="form-text">
                        <i class="bi bi-info-circle me-1"></i>
                        Fecha de fin del periodo de facturación a consultar
                      </div>
                    </div>
                  </div>

                  <!-- Billing Period Reports Table -->
                  <div class="row mt-4" v-if="fecha_inicio_periodo && fecha_fin_periodo">
                    <div class="col-12">
                      <hr class="my-3">
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <h6 class="mb-0">
                          <i class="bi bi-table me-2"></i>
                          Dispositivos con Reportes en el Periodo
                        </h6>
                        <span class="badge bg-primary fs-6">
                          Total de Dispositivos Informados: {{ totalBillingReportsInPeriod }}
                        </span>
                      </div>

                      <!-- Loading state -->
                      <div v-if="loadingBillingReports" class="text-center py-4">
                        <div class="spinner-border spinner-border-sm text-primary" role="status">
                          <span class="visually-hidden">Cargando reportes...</span>
                        </div>
                        <p class="text-muted mt-2 small">Cargando reportes del periodo...</p>
                      </div>

                      <!-- No reports -->
                      <div v-else-if="!billingPeriodReports || billingPeriodReports.length === 0" class="text-center py-4">
                        <i class="bi bi-inbox fs-4 text-muted"></i>
                        <p class="text-muted mt-2 mb-0">No hay reportes en este periodo</p>
                        <p class="text-muted small">Intente con un rango de fechas diferente</p>
                      </div>

                      <!-- Reports table -->
                      <div v-else class="table-responsive">
                        <table class="table table-hover table-sm mb-0">
                          <thead class="table-light">
                            <tr>
                              <th>Device ID</th>
                              <th>Nombre del Dispositivo</th>
                              <th>Patente</th>
                              <th class="text-end">Cantidad de Informes</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="device in billingPeriodReports" :key="device.device_id">
                              <td>
                                <code class="text-primary">{{ device.device_id }}</code>
                              </td>
                              <td>
                                <span v-if="device.device_name" class="fw-medium">
                                  {{ device.device_name }}
                                </span>
                                <span v-else class="text-muted fst-italic">Sin nombre</span>
                              </td>
                              <td>
                                <span class="badge bg-secondary">
                                  <i class="bi bi-car-front me-1"></i>
                                  {{ device.license_plate }}
                                </span>
                              </td>
                              <td class="text-end">
                                <span class="badge bg-success fs-6">
                                  <i class="bi bi-file-text me-1"></i>
                                  {{ device.total_reports }}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                          <tfoot class="table-light">
                            <tr>
                              <td colspan="3" class="text-end fw-bold">Total:</td>
                              <td class="text-end">
                                <span class="badge bg-primary fs-6">
                                  {{ totalBillingReports }}
                                </span>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Database Credentials Card -->
          <div v-if="currentClient.nombre_base_datos || currentClient.usuario_base_datos" class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header">
                  <h5 class="mb-0">
                    <i class="bi bi-database me-2"></i>
                    Credenciales de Base de Datos
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-4">
                      <p class="mb-2"><strong>Nombre de Base de Datos:</strong></p>
                      <p class="text-muted">{{ currentClient.nombre_base_datos || 'No especificado' }}</p>
                    </div>
                    <div class="col-md-4">
                      <p class="mb-2"><strong>Usuario:</strong></p>
                      <p class="text-muted">{{ currentClient.usuario_base_datos || 'No especificado' }}</p>
                    </div>
                    <div class="col-md-4">
                      <p class="mb-2"><strong>Clave:</strong></p>
                      <p class="text-muted">
                        <i class="bi bi-lock-fill me-1"></i>
                        {{ currentClient.clave_base_datos ? '••••••••' : 'No especificada' }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Associated Scripts Card -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <h5 class="mb-0">
                    <i class="bi bi-play-circle me-2"></i>
                    Scripts Asociados
                  </h5>
                  <router-link
                    :to="{ name: 'ScriptCreate', query: { client_id: currentClient.id } }"
                    class="btn btn-sm btn-primary"
                  >
                    <i class="bi bi-plus-lg me-1"></i>
                    Nuevo Script
                  </router-link>
                </div>
                <div class="card-body">
                  <!-- Loading scripts -->
                  <div v-if="loadingScripts" class="text-center py-4">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                      <span class="visually-hidden">Cargando scripts...</span>
                    </div>
                    <p class="text-muted mt-2 small">Cargando scripts...</p>
                  </div>

                  <!-- No scripts -->
                  <div v-else-if="!clientScripts || clientScripts.length === 0" class="text-center py-4">
                    <i class="bi bi-inbox fs-3 text-muted"></i>
                    <p class="text-muted mt-2">No hay scripts asociados a este cliente</p>
                    <router-link
                      :to="{ name: 'ScriptCreate', query: { client_id: currentClient.id } }"
                      class="btn btn-sm btn-primary"
                    >
                      <i class="bi bi-plus-lg me-1"></i>
                      Crear primer script
                    </router-link>
                  </div>

                  <!-- Scripts table -->
                  <div v-else class="table-responsive">
                    <table class="table table-hover table-sm mb-0">
                      <thead class="table-light">
                        <tr>
                          <th>ID</th>
                          <th>Estado</th>
                          <th>PID</th>
                          <th>Base de Datos</th>
                          <th>Iniciado</th>
                          <th>Último Heartbeat</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="script in clientScripts" :key="script.id">
                          <td>
                            <span class="badge bg-secondary">{{ script.id }}</span>
                          </td>
                          <td>
                            <span :class="getStatusBadgeClass(script.status)">
                              <i :class="getStatusIcon(script.status)" class="me-1"></i>
                              {{ getStatusText(script.status) }}
                            </span>
                          </td>
                          <td>
                            <span v-if="script.pid" class="badge bg-dark">
                              {{ script.pid }}
                            </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td>
                            <small class="text-muted">
                              <i class="bi bi-database me-1"></i>
                              {{ script.geotab_database }}
                            </small>
                          </td>
                          <td>
                            <small v-if="script.started_at">
                              {{ formatDate(script.started_at) }}
                            </small>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td>
                            <small v-if="script.last_heartbeat">
                              {{ formatDate(script.last_heartbeat) }}
                            </small>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td>
                            <div class="btn-group btn-group-sm" role="group">
                              <!-- View logs -->
                              <router-link
                                :to="`/scripts/${script.id}/logs`"
                                class="btn btn-outline-primary"
                                title="Ver logs"
                              >
                                <i class="bi bi-file-text"></i>
                              </router-link>

                              <!-- Edit (only if not running) -->
                              <router-link
                                v-if="script.status !== 'running'"
                                :to="`/scripts/${script.id}/edit`"
                                class="btn btn-outline-secondary"
                                title="Editar"
                              >
                                <i class="bi bi-pencil"></i>
                              </router-link>

                              <!-- Stop (only if running) -->
                              <button
                                v-if="script.status === 'running'"
                                type="button"
                                class="btn btn-outline-warning"
                                @click="stopScript(script.id)"
                                title="Detener"
                              >
                                <i class="bi bi-pause-circle"></i>
                              </button>

                              <!-- Restart -->
                              <button
                                v-if="script.status === 'running' || script.status === 'stopped' || script.status === 'failed'"
                                type="button"
                                class="btn btn-outline-success"
                                @click="restartScript(script.id)"
                                title="Reiniciar"
                              >
                                <i class="bi bi-arrow-clockwise"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Associated Devices Card -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <h5 class="mb-0">
                    <i class="bi bi-device-hdd me-2"></i>
                    Dispositivos Asociados
                  </h5>
                  <div class="d-flex gap-2">
                    <span class="badge bg-info fs-6">
                      Total: {{ currentClient.device_count || 0 }}
                    </span>
                    <span class="badge bg-success fs-6">
                      Activos: {{ currentClient.active_devices || 0 }}
                    </span>
                  </div>
                </div>
                <div class="card-body">
                  <!-- Loading devices -->
                  <div v-if="loadingDevices" class="text-center py-4">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                      <span class="visually-hidden">Cargando dispositivos...</span>
                    </div>
                    <p class="text-muted mt-2 small">Cargando dispositivos...</p>
                  </div>

                  <!-- No devices -->
                  <div v-else-if="!clientDevices || clientDevices.length === 0" class="text-center py-4">
                    <i class="bi bi-inbox fs-3 text-muted"></i>
                    <p class="text-muted mt-2">No hay dispositivos asociados a este cliente</p>
                    <p class="text-muted small">
                      Los dispositivos se detectan automáticamente al procesar los logs de los scripts
                    </p>
                  </div>

                  <!-- Devices table -->
                  <div v-else class="table-responsive">
                    <table class="table table-hover table-sm mb-0">
                      <thead class="table-light">
                        <tr>
                          <th style="width: 50px;"></th>
                          <th>ID Dispositivo</th>
                          <th>Nombre</th>
                          <th>Patente</th>
                          <th>Asignado</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody v-for="device in clientDevices" :key="device.id">
                        <tr :class="{ 'table-active': expandedDeviceId === device.id }">
                          <td>
                            <button
                              type="button"
                              class="btn btn-sm btn-link p-0"
                              @click="toggleDeviceReports(device)"
                              :title="expandedDeviceId === device.id ? 'Ocultar reportes' : 'Ver reportes'"
                            >
                              <i
                                class="bi"
                                :class="expandedDeviceId === device.id ? 'bi-chevron-down' : 'bi-chevron-right'"
                              ></i>
                            </button>
                          </td>
                          <td>
                            <code class="text-primary">{{ device.device_id }}</code>
                          </td>
                          <td>
                            <span v-if="device.device_name" class="fw-medium">
                              {{ device.device_name }}
                            </span>
                            <span v-else class="text-muted fst-italic">Sin nombre</span>
                          </td>
                          <td>
                            <span class="badge bg-secondary">
                              <i class="bi bi-car-front me-1"></i>
                              {{ device.license_plate }}
                            </span>
                          </td>
                          <td>
                            <small v-if="device.assigned_at">
                              {{ formatDate(device.assigned_at) }}
                            </small>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td>
                            <span
                              class="badge"
                              :class="device.active ? 'bg-success' : 'bg-secondary'"
                            >
                              <i
                                class="bi me-1"
                                :class="device.active ? 'bi-check-circle' : 'bi-pause-circle'"
                              ></i>
                              {{ device.active ? 'Activo' : 'Inactivo' }}
                            </span>
                          </td>
                        </tr>
                        <tr v-if="expandedDeviceId === device.id" class="expanded-row">
                            <td colspan="6" class="p-0">
                              <div class="expanded-content p-3">
                                <!-- Loading Reports -->
                                <div v-if="loadingReports" class="text-center py-3">
                                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                                    <span class="visually-hidden">Cargando reportes...</span>
                                  </div>
                                  <p class="text-muted mt-2 mb-0 small">Cargando reportes...</p>
                                </div>

                                <!-- No Reports -->
                                <div v-else-if="!deviceReports || deviceReports.length === 0" class="text-center py-3">
                                  <i class="bi bi-inbox fs-5 text-muted"></i>
                                  <p class="text-muted mt-2 mb-0 small">No hay reportes disponibles</p>
                                </div>

                                <!-- Reports Table -->
                                <div v-else>
                                  <h6 class="mb-3">
                                    <i class="bi bi-bar-chart me-2"></i>
                                    Reportes del Dispositivo
                                  </h6>
                                  <div class="table-responsive">
                                    <table class="table table-sm table-bordered mb-3">
                                      <thead class="table-light">
                                        <tr>
                                          <th>Fecha</th>
                                          <th>Cantidad de Informes</th>
                                          <th>Tipo de Script</th>
                                          <th>Script Execution ID</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr v-for="report in deviceReports" :key="report.id">
                                          <td>
                                            <i class="bi bi-calendar3 me-1"></i>
                                            <small>{{ formatDate(report.report_date) }}</small>
                                          </td>
                                          <td>
                                            <span class="badge bg-primary">
                                              <i class="bi bi-file-text me-1"></i>
                                              {{ report.report_count || 0 }}
                                            </span>
                                          </td>
                                          <td>
                                            <span v-if="report.script_type" class="badge" :class="report.script_type === 'data_feed' ? 'bg-info' : 'bg-warning'">
                                              <i class="bi" :class="report.script_type === 'data_feed' ? 'bi-database-fill' : 'bi-code-square'"></i>
                                              {{ report.script_type }}
                                            </span>
                                            <span v-else class="text-muted">-</span>
                                          </td>
                                          <td>
                                            <span v-if="report.script_execution_id" class="badge bg-secondary">
                                              #{{ report.script_execution_id }}
                                            </span>
                                            <span v-else class="text-muted">-</span>
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>

                                  <!-- Summary -->
                                  <div class="row">
                                    <div class="col-md-6">
                                      <small class="text-muted">
                                        <strong>Total de Registros:</strong> {{ deviceReports.length }}
                                      </small>
                                    </div>
                                    <div class="col-md-6">
                                      <small class="text-muted">
                                        <strong>Total de Informes:</strong> {{ totalReportCount }}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Not Found -->
        <div v-else class="text-center py-5">
          <i class="bi bi-person-x fs-1 text-muted mb-3"></i>
          <h5>Cliente no encontrado</h5>
          <p class="text-muted mb-3">
            El cliente solicitado no existe o ha sido eliminado
          </p>
          <router-link to="/clients" class="btn btn-primary">
            <i class="bi bi-arrow-left me-1"></i>
            Volver a la lista
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useClientStore } from '../../stores/clients'
import { useScriptStore } from '../../stores/scripts'
import { clientsApi } from '../../services/api'

export default {
  name: 'ClientDetail',
  setup() {
    const route = useRoute()
    const clientStore = useClientStore()
    const scriptStore = useScriptStore()

    const currentClient = computed(() => clientStore.currentClient)
    const loading = computed(() => clientStore.loading)

    // Scripts state
    const clientScripts = ref([])
    const loadingScripts = ref(false)
    const autoRefreshInterval = ref(null)

    // Devices state
    const clientDevices = ref([])
    const loadingDevices = ref(false)

    // Device Reports state
    const expandedDeviceId = ref(null)
    const deviceReports = ref([])
    const loadingReports = ref(false)

    // Billing period state
    const fecha_inicio_periodo = ref('')
    const fecha_fin_periodo = ref('')
    const billingPeriodReports = ref([])
    const loadingBillingReports = ref(false)

    // Computed: Total report count
    const totalReportCount = computed(() => {
      return deviceReports.value.reduce((sum, report) => sum + (report.report_count || 0), 0)
    })

    // Computed: Total billing period reports
    const totalBillingReports = computed(() => {
      return billingPeriodReports.value.reduce((sum, device) => sum + 1*(device.total_reports || 0), 0)
    })

    const totalBillingReportsInPeriod = computed(() => {
      return billingPeriodReports.value.length
    })

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const calculateCurrentBillingPeriod = () => {
      if (!currentClient.value || !currentClient.value.fecha_inicio_facturacion) {
        fecha_inicio_periodo.value = ''
        fecha_fin_periodo.value = ''
        return
      }

      const fechaInicio = new Date(currentClient.value.fecha_inicio_facturacion)
      const diaFacturacion = fechaInicio.getDate()
      const hoy = new Date()

      // Calcular el inicio del periodo actual
      let inicioPeriodo = new Date(hoy.getFullYear(), hoy.getMonth(), diaFacturacion)

      // Si el día de facturación aún no llegó este mes, el periodo actual empezó el mes anterior
      if (hoy.getDate() < diaFacturacion) {
        inicioPeriodo.setMonth(inicioPeriodo.getMonth() - 1)
      }

      // Calcular el fin del periodo (un día antes del siguiente inicio)
      const finPeriodo = new Date(inicioPeriodo)
      finPeriodo.setMonth(finPeriodo.getMonth() + 1)
      finPeriodo.setDate(finPeriodo.getDate() - 1)

      // Formatear las fechas para input type="date" (YYYY-MM-DD)
      fecha_inicio_periodo.value = inicioPeriodo.toISOString().split('T')[0]
      fecha_fin_periodo.value = finPeriodo.toISOString().split('T')[0]
    }

    const goToPreviousPeriod = () => {
      if (!fecha_inicio_periodo.value) return

      // Parsear la fecha de inicio actual
      const fechaActual = new Date(fecha_inicio_periodo.value + 'T00:00:00')

      // Restar un mes
      const nuevaFechaInicio = new Date(fechaActual)
      nuevaFechaInicio.setMonth(nuevaFechaInicio.getMonth() - 1)

      // Calcular el fin del periodo (un día antes del siguiente inicio)
      const nuevaFechaFin = new Date(nuevaFechaInicio)
      nuevaFechaFin.setMonth(nuevaFechaFin.getMonth() + 1)
      nuevaFechaFin.setDate(nuevaFechaFin.getDate() - 1)

      // Actualizar las fechas
      fecha_inicio_periodo.value = nuevaFechaInicio.toISOString().split('T')[0]
      fecha_fin_periodo.value = nuevaFechaFin.toISOString().split('T')[0]
    }

    const goToNextPeriod = () => {
      if (!fecha_inicio_periodo.value) return

      // Parsear la fecha de inicio actual
      const fechaActual = new Date(fecha_inicio_periodo.value + 'T00:00:00')

      // Sumar un mes
      const nuevaFechaInicio = new Date(fechaActual)
      nuevaFechaInicio.setMonth(nuevaFechaInicio.getMonth() + 1)

      // Calcular el fin del periodo (un día antes del siguiente inicio)
      const nuevaFechaFin = new Date(nuevaFechaInicio)
      nuevaFechaFin.setMonth(nuevaFechaFin.getMonth() + 1)
      nuevaFechaFin.setDate(nuevaFechaFin.getDate() - 1)

      // Actualizar las fechas
      fecha_inicio_periodo.value = nuevaFechaInicio.toISOString().split('T')[0]
      fecha_fin_periodo.value = nuevaFechaFin.toISOString().split('T')[0]
    }

    const fetchClientScripts = async () => {
      const clientId = parseInt(route.params.id)
      if (!clientId) return

      loadingScripts.value = true
      try {
        const scripts = await scriptStore.fetchScripts({ client_id: clientId })
        clientScripts.value = scripts
      } catch (error) {
        console.error('Error loading client scripts:', error)
      } finally {
        loadingScripts.value = false
      }
    }

    const fetchClientDevices = async () => {
      const clientId = parseInt(route.params.id)
      if (!clientId) return

      loadingDevices.value = true
      try {
        const devices = await clientStore.fetchClientDevices(clientId)
        clientDevices.value = devices
      } catch (error) {
        console.error('Error loading client devices:', error)
      } finally {
        loadingDevices.value = false
      }
    }

    const fetchBillingPeriodReports = async () => {
      const clientId = parseInt(route.params.id)
      if (!clientId || !fecha_inicio_periodo.value || !fecha_fin_periodo.value) {
        billingPeriodReports.value = []
        return
      }

      loadingBillingReports.value = true
      try {
        const reports = await clientsApi.getBillingPeriodReports(
          clientId,
          fecha_inicio_periodo.value,
          fecha_fin_periodo.value
        )
        billingPeriodReports.value = reports
      } catch (error) {
        console.error('Error loading billing period reports:', error)
        billingPeriodReports.value = []
      } finally {
        loadingBillingReports.value = false
      }
    }

    const toggleDeviceReports = async (device) => {
      // If clicking on already expanded device, collapse it
      if (expandedDeviceId.value === device.id) {
        expandedDeviceId.value = null
        deviceReports.value = []
        return
      }

      // Expand new device and load reports
      expandedDeviceId.value = device.id
      deviceReports.value = []
      loadingReports.value = true

      try {
        const clientId = parseInt(route.params.id)
        const reports = await clientStore.fetchDeviceReports(clientId, device.id)
        deviceReports.value = reports
      } catch (error) {
        console.error('Error loading device reports:', error)
      } finally {
        loadingReports.value = false
      }
    }

    const stopScript = async (scriptId) => {
      if (confirm('¿Estás seguro de que deseas detener este script?')) {
        try {
          await scriptStore.stopScript(scriptId)
          await fetchClientScripts()
        } catch (error) {
          console.error('Error stopping script:', error)
        }
      }
    }

    const restartScript = async (scriptId) => {
      if (confirm('¿Estás seguro de que deseas reiniciar este script?')) {
        try {
          await scriptStore.restartScript(scriptId)
          await fetchClientScripts()
        } catch (error) {
          console.error('Error restarting script:', error)
        }
      }
    }

    const getStatusBadgeClass = (status) => {
      const classes = {
        pending: 'badge bg-secondary',
        running: 'badge bg-success',
        stopped: 'badge bg-warning',
        failed: 'badge bg-danger',
        completed: 'badge bg-info'
      }
      return classes[status] || 'badge bg-secondary'
    }

    const getStatusIcon = (status) => {
      const icons = {
        pending: 'bi-hourglass-split',
        running: 'bi-play-circle-fill',
        stopped: 'bi-pause-circle-fill',
        failed: 'bi-exclamation-circle-fill',
        completed: 'bi-check-circle-fill'
      }
      return icons[status] || 'bi-question-circle'
    }

    const getStatusText = (status) => {
      const texts = {
        pending: 'Pendiente',
        running: 'En Ejecución',
        stopped: 'Detenido',
        failed: 'Fallido',
        completed: 'Completado'
      }
      return texts[status] || status
    }

    const startAutoRefresh = () => {
      autoRefreshInterval.value = setInterval(() => {
        fetchClientScripts()
      }, 10000) // Refresh every 10 seconds
    }

    const stopAutoRefresh = () => {
      if (autoRefreshInterval.value) {
        clearInterval(autoRefreshInterval.value)
        autoRefreshInterval.value = null
      }
    }

    // Watch for changes in billing period dates
    watch([fecha_inicio_periodo, fecha_fin_periodo], () => {
      fetchBillingPeriodReports()
    })

    onMounted(async () => {
      const clientId = parseInt(route.params.id)
      if (clientId) {
        await clientStore.fetchClient(clientId)
        calculateCurrentBillingPeriod()
        await fetchClientScripts()
        await fetchClientDevices()
        await fetchBillingPeriodReports()
        startAutoRefresh()
      }
    })

    onUnmounted(() => {
      stopAutoRefresh()
    })

    return {
      currentClient,
      loading,
      clientScripts,
      loadingScripts,
      clientDevices,
      loadingDevices,
      expandedDeviceId,
      deviceReports,
      loadingReports,
      totalReportCount,
      fecha_inicio_periodo,
      fecha_fin_periodo,
      billingPeriodReports,
      loadingBillingReports,
      totalBillingReports,
      totalBillingReportsInPeriod,
      formatDate,
      goToPreviousPeriod,
      goToNextPeriod,
      toggleDeviceReports,
      stopScript,
      restartScript,
      getStatusBadgeClass,
      getStatusIcon,
      getStatusText
    }
  }
}
</script>

<style scoped>
/* Expanded row styles */
.expanded-row {
  background-color: var(--bs-light);
}

.expanded-content {
  background-color: var(--bs-white);
  border-left: 3px solid var(--bs-primary);
  border-radius: 4px;
  margin: 0.5rem;
}

/* Button link styles for chevron */
.btn-link {
  text-decoration: none;
  color: var(--bs-secondary);
  transition: color 0.2s;
}

.btn-link:hover {
  color: var(--bs-primary);
}

/* Active row highlight */
.table-active {
  background-color: rgba(13, 110, 253, 0.05);
}

/* Dark theme support */
:root[data-theme="dark"] .expanded-content {
  background-color: var(--bs-dark);
  border-left-color: var(--bs-info);
}

:root[data-theme="dark"] .expanded-row {
  background-color: rgba(0, 0, 0, 0.2);
}
</style>
